import { useRef } from 'react';
import { getChartTitle } from '@/shared/copy/charts';
import { getErrorCopy } from '@/shared/copy/errors';
import { s, spacing, typographyTokens } from '@/theme/tokens';
import { CHART_EMPTY_STATE_NO_DATA_LABEL, ChartEmptyState } from '@/ui/composites';
import { Box, PieChart, Text } from '@/ui/primitives';
import { buildStatusPieChartGeometry } from './chartPanelGeometry';
import {
  buildChartPanelCenteredStateSx,
  buildStatusPieBreakdownLayoutRecipe,
  chartPanelGridContentSx,
} from './chartPanelSurfaceSx';
import { ChartPanelTitle } from './ChartPanelTitle';
import { MetricsChartPanelFrame } from './MetricsChartPanelFrame';
import { type StackedMetricsMiniPanelsLayout } from './StackedMetricsStatusChartSection.layout';
import { renderStatusPieLoadingState } from './StackedMetricsStatusChartSection.states';
import { useChartPanelMeasuredSize } from './useChartPanelMeasuredSize';
import type { StatusPieDatum } from './StackedMetricsStatusChartSection.panelTypes';
import type { ReactElement } from 'react';

type StatusPieBreakdownPanelProps = {
  loading: boolean;
  showPlaceholder?: boolean;
  showErrorState: boolean;
  piePanelHeight: number;
  pieChartVisualHeight: number;
  miniPanels: StackedMetricsMiniPanelsLayout;
  headerToChartGap: number;
  miniMetricEmptyTitle: string;
  statusPieData: StatusPieDatum[];
  statusLegend: ReactElement | null;
  legendMarginTop?: string;
};

const runCountFormatter = new Intl.NumberFormat('en-GB');
const statusLegendRowGapPx = spacing.xs;
const statusLegendRowHeightPx = 22;
const statusPieCentreLabelOffsetY = s('none');
const statusPieFallbackChartHeight = 220;
const statusPieHorizontalInsetPx = spacing.xl;
const statusPieCentreValueFontSize = { xs: 24, xl: 26 } as const;
const statusPieCentreLabelFontSize = 13;

export const StatusPieBreakdownPanel = ({
  loading,
  showPlaceholder = false,
  showErrorState,
  piePanelHeight,
  pieChartVisualHeight,
  miniPanels,
  headerToChartGap,
  miniMetricEmptyTitle: _miniMetricEmptyTitle,
  statusPieData,
  statusLegend,
  legendMarginTop = s('none'),
}: StatusPieBreakdownPanelProps): ReactElement => {
  const chartSlotRef = useRef<HTMLDivElement | null>(null);
  const fallbackChartSize = Math.min(pieChartVisualHeight, statusPieFallbackChartHeight);
  const chartSlotSize = useChartPanelMeasuredSize({
    fallbackHeight: fallbackChartSize,
    fallbackWidth: fallbackChartSize,
    targetRef: chartSlotRef,
  });
  const runCount = statusPieData.reduce((sum, entry) => sum + entry.value, 0);
  const formattedRunCount = runCountFormatter.format(runCount);
  const runCountLabel = runCount === 1 ? 'Run' : 'Runs';
  const legendRowCount = statusLegend ? statusPieData.length : 0;
  const statusLegendSlotHeight =
    legendRowCount > 0
      ? legendRowCount * statusLegendRowHeightPx +
        Math.max(0, legendRowCount - 1) * statusLegendRowGapPx
      : 0;
  const pieChartGeometry = buildStatusPieChartGeometry({
    horizontalInsetPx: statusPieHorizontalInsetPx,
    slotHeight: chartSlotSize.height,
    slotWidth: chartSlotSize.width,
  });
  const pieLayoutRecipe = buildStatusPieBreakdownLayoutRecipe({
    centreOffsetY: statusPieCentreLabelOffsetY,
    legendMarginTop,
    legendSlotHeight: statusLegendSlotHeight,
    visualSize: pieChartGeometry.visualSize,
  });

  return (
    <MetricsChartPanelFrame
      height={piePanelHeight}
      header={
        <ChartPanelTitle
          title={getChartTitle('statusBreakdown')}
          component="h2"
          fontSizePx={14}
          fontWeight={500}
          justifyContent="center"
          showInfoIcon={false}
        />
      }
      headerPaddingX={s('xl')}
      headerPaddingTop={s('md')}
      headerPaddingBottom={s('xs')}
      contentPaddingX={s('xl')}
      contentPaddingTop={s('xs')}
      contentPaddingBottom={s('lg')}
      headerSx={{
        minHeight: `${miniPanels.headerHeight}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      contentSx={chartPanelGridContentSx}
    >
      {showPlaceholder || loading ? (
        renderStatusPieLoadingState(pieChartVisualHeight)
      ) : showErrorState ? (
        <Box
          sx={buildChartPanelCenteredStateSx({
            height: pieChartGeometry.effectiveVisualHeight,
            minHeight: pieChartGeometry.effectiveVisualHeight,
            layout: 'grid',
            offsetY: headerToChartGap,
            widthFull: true,
          })}
        >
          <ChartEmptyState title={getErrorCopy('banner.chartLoadFailed')} variant="metricCard" />
        </Box>
      ) : statusPieData.length > 0 ? (
        <Box sx={pieLayoutRecipe.panelBodySx}>
          <Box ref={chartSlotRef} sx={pieLayoutRecipe.chartSlotSx}>
            <Box sx={pieLayoutRecipe.chartVisualFrameSx}>
              <PieChart
                data={statusPieData}
                height={pieChartGeometry.visualSize}
                innerRadius={pieChartGeometry.innerRadius}
                outerRadius={pieChartGeometry.outerRadius}
                showLegend={false}
                width={pieChartGeometry.visualSize}
              />
              <Box
                aria-label={`${formattedRunCount} ${runCountLabel.toLowerCase()}`}
                sx={pieLayoutRecipe.centreOverlaySx}
              >
                <Box sx={pieLayoutRecipe.centreValueStackSx}>
                  <Text
                    role="sectionTitle"
                    component="span"
                    align="center"
                    sx={{
                      fontSize: statusPieCentreValueFontSize,
                      fontWeight: typographyTokens.metricValue.fontWeight,
                      fontVariantNumeric: 'tabular-nums',
                      lineHeight: 1.05,
                    }}
                  >
                    {formattedRunCount}
                  </Text>
                  <Text
                    role="bodyStrong"
                    color="textSecondary"
                    component="span"
                    align="center"
                    sx={{
                      fontSize: (muiTheme) =>
                        muiTheme.typography.pxToRem(statusPieCentreLabelFontSize),
                      fontWeight: typographyTokens.body.fontWeight,
                      lineHeight: 1.2,
                    }}
                  >
                    {runCountLabel}
                  </Text>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box sx={pieLayoutRecipe.legendSlotSx}>{statusLegend}</Box>
        </Box>
      ) : (
        <Box
          sx={buildChartPanelCenteredStateSx({
            height: pieChartVisualHeight,
            minHeight: pieChartVisualHeight,
            layout: 'grid',
            offsetY: headerToChartGap,
            widthFull: true,
          })}
        >
          <ChartEmptyState title={CHART_EMPTY_STATE_NO_DATA_LABEL} variant="metricCard" />
        </Box>
      )}
    </MetricsChartPanelFrame>
  );
};
